import { Texture } from './texture';
import { Shader, warpShader } from './shader';
import {getInverse, getSquareToQuad, multiply} from "./matrix";

interface CanvasState {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  isInitialized: boolean;
  texture: Texture | null;
  spareTexture: Texture | null;
  extraTexture: Texture | null;
  flippedShader: Shader | null;
}

export class WebGLCanvas {
  private _state: CanvasState;

  constructor() {
    const canvas = document.createElement('canvas');
    let gl = canvas.getContext('experimental-webgl', {premultipliedAlpha: false}) as WebGLRenderingContext;

    if (!gl) {
      throw new Error('This browser does not support WebGL');
    }

    this._state = {
      canvas,
      gl,
      isInitialized: false,
      texture: null,
      spareTexture: null,
      extraTexture: null,
      flippedShader: null,
    };
  }

  public clean() {
    this._state.canvas.remove();
    this._state.texture?.destroy();
    this._state.spareTexture?.destroy();
    this._state.extraTexture?.destroy();
    this._state.flippedShader?.destroy(this._state.gl);
    this._state.isInitialized = false;
  }

  public getHtmlCanvas(): HTMLCanvasElement {
    return this._state.canvas;
  }

  private initialize(width: number, height: number) {
    const gl = this._state.gl;
    let type = gl.UNSIGNED_BYTE;

    if (gl.getExtension('OES_texture_float') && gl.getExtension('OES_texture_float_linear')) {
      const testTexture = new Texture(gl, 100, 100, gl.RGBA, gl.FLOAT);
      try {
        testTexture.drawTo(() => {
          // @ts-ignore
          type = gl.FLOAT
        });
      } catch (e) {}
      testTexture.destroy();
    }

    this._state.texture?.destroy();
    this._state.spareTexture?.destroy();

    this._state.canvas.width = width;
    this._state.canvas.height = height;
    this._state.texture = new Texture(gl, width, height, gl.RGBA, type);
    this._state.spareTexture = new Texture(gl, width, height, gl.RGBA, type);
    this._state.extraTexture = this._state.extraTexture || new Texture(gl, 0, 0, gl.RGBA, type);
    this._state.flippedShader = this._state.flippedShader || new Shader(gl, undefined, '\
    uniform sampler2D texture;\
    varying vec2 texCoord;\
    void main() {\
      gl_FragColor = texture2D(texture, vec2(texCoord.x, 1.0 - texCoord.y));\
    }\
  ');
    this._state.isInitialized = true;
  }

  draw(texture: Texture, width?: number, height?: number) {
    if (!this._state.isInitialized || texture.width !== this._state.canvas.width || texture.height !== this._state.canvas.height) {
      this.initialize(width ?? texture.width, height ?? texture.height);
    }

    texture.use();
    this._state.texture?.drawTo(() => {
      Shader.getDefaultShader(this._state.gl).drawRect(this._state.gl);
    });
  }

  update() {
    this._state.texture?.use();
    this._state.flippedShader?.drawRect(this._state.gl);
  }

  public texture(element: HTMLCanvasElement): Texture {
    return Texture.fromElement(this._state.gl, element)
  }

  public perspective(previous: Array<number>, next: Array<number>): void {
    // @ts-ignore
    const a = getSquareToQuad(...next);
    // @ts-ignore
    const b = getSquareToQuad(...previous);
    const c = multiply(getInverse(a), b);

    this.matrixWarp(c);
  }

  private matrixWarp(matrix: Array<Array<number>> | Array<number>, inverse?: boolean, useTextureSpace?: boolean): void {
    const gl = this._state.gl

    // @ts-ignore
    gl.matrixWarp = gl.matrixWarp || warpShader(this._state.gl,'\
    uniform mat3 matrix;\
    uniform bool useTextureSpace;\
  ', '\
    if (useTextureSpace) coord = coord / texSize * 2.0 - 1.0;\
    vec3 warp = matrix * vec3(coord, 1.0);\
    coord = warp.xy / warp.z;\
    if (useTextureSpace) coord = (coord * 0.5 + 0.5) * texSize;\
  ');

    matrix = matrix.flat();

    if (matrix.length === 4) {
      matrix = [matrix[0], matrix[1], 0, matrix[2], matrix[3], 0, 0, 0, 1];
    } else if (matrix.length !== 9) {
      throw new Error('Can only warp with a 2x2 or 3x3 matrix');
    }

    // @ts-ignore
    this.simpleShader(gl.matrixWarp, {
      matrix: inverse ? getInverse(matrix) : matrix,
      texSize: [this._state.canvas.width, this._state.canvas.height],
      // @ts-ignore
      useTextureSpace: useTextureSpace | 0,
    })
  }

  private simpleShader(shader: any, uniforms: any, textureIn?: Texture, textureOut?: Texture): void {
    const activeTexture = textureIn || this._state.texture;
    activeTexture?.use();
    this._state.spareTexture?.drawTo(() => {
      shader.uniforms(this._state.gl, uniforms).drawRect(this._state.gl);
    });
    const textureOutActive = textureOut || this._state.texture;
    if (!textureOutActive) throw new Error('No output texture');
    this._state.spareTexture?.swapWith(textureOutActive);
  }
}
